import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, PartyPopper, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, get } from "@/lib/api";
import { cn } from "@/lib/utils";
import { EASE } from "@/components/motion/Motion";

const SEEN = "tl_gs_seen_done";
const COLLAPSED = "tl_gs_collapsed";
export const useGettingStarted = () => useQuery({ queryKey: ["getting-started"], queryFn: () => get("/getting-started"), refetchInterval: 20000 });

export async function reopenGettingStarted(qc) {
  localStorage.setItem(COLLAPSED, "0");
  await api.post("/getting-started/dismiss", { dismissed: false });
  qc.invalidateQueries({ queryKey: ["getting-started"] });
}

function Burst() {
  return (
    <span className="pointer-events-none absolute inset-0" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <motion.span key={i} className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full bg-success"
          initial={{ x: 0, y: 0, opacity: 1 }} animate={{ x: Math.cos((i / 6) * 6.28) * 16, y: Math.sin((i / 6) * 6.28) * 16, opacity: 0 }}
          transition={{ duration: 0.6, ease: EASE }} />
      ))}
    </span>
  );
}

function Step({ s, i, fresh }) {
  const nav = useNavigate();
  return (
    <li className={cn("flex items-center gap-3 rounded-[6px] border px-3 py-2.5 transition-colors duration-300", s.done ? "border-[#CADBCF] bg-success-tint/60" : "border-line bg-white")} data-testid={`gs-step-${s.key}`} data-done={s.done ? "true" : "false"}>
      <span className="relative flex h-6 w-6 shrink-0 items-center justify-center">
        {s.done ? (
          <motion.span initial={fresh ? { scale: 0 } : false} animate={{ scale: [fresh ? 0 : 1, 1.25, 1] }} transition={{ duration: 0.45, ease: EASE }}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-success text-white"><Check size={14} aria-hidden="true" /></motion.span>
        ) : <span className="flex h-6 w-6 items-center justify-center rounded-full border border-line text-xs text-ink-secondary">{i + 1}</span>}
        {fresh && <Burst />}
      </span>
      <span className={cn("flex-1 text-sm", s.done ? "text-ink-secondary line-through decoration-success/40" : "text-ink")}>{s.title}<span className="sr-only">{s.done ? " (done)" : " (to do)"}</span></span>
      {!s.done && <Button size="sm" variant="outline" className="h-8" onClick={() => nav(s.href)} data-testid={`gs-step-${s.key}-btn`}>{s.cta}</Button>}
    </li>
  );
}

export function GettingStartedCard() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data } = useGettingStarted();
  const [collapsed, setCollapsed] = useState(localStorage.getItem(COLLAPSED) === "1");
  const [fresh, setFresh] = useState([]);
  const first = useRef(true);
  useEffect(() => {
    if (!data) return;
    const seen = JSON.parse(localStorage.getItem(SEEN) || "[]");
    const now = data.steps.filter((s) => s.done).map((s) => s.key);
    if (!first.current || seen.length) setFresh(now.filter((k) => !seen.includes(k)));
    first.current = false;
    localStorage.setItem(SEEN, JSON.stringify(now));
  }, [data]);
  if (!data || data.dismissed) return null;
  const toggle = () => { localStorage.setItem(COLLAPSED, collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  const dismiss = async () => { await api.post("/getting-started/dismiss", { dismissed: true }); qc.invalidateQueries({ queryKey: ["getting-started"] }); };
  return (
    <motion.section layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="panel p-5" data-testid="getting-started-card" id="getting-started">
      <div className="flex items-center gap-3">
        <ProgressRing done={data.done} total={data.total} size={40} />
        <div className="flex-1">
          <h2 className="text-base font-semibold text-ink">{data.complete ? "You're all set" : "Getting started"}</h2>
          <p className="text-xs text-ink-secondary" data-testid="gs-progress-text">{data.done} of {data.total} done · steps tick off automatically as you work</p>
        </div>
        <Button variant="ghost" size="sm" onClick={toggle} aria-expanded={!collapsed} aria-controls="gs-steps" data-testid="gs-collapse-btn">
          <ChevronDown size={16} className={cn("transition-transform duration-200", collapsed && "-rotate-90")} aria-hidden="true" />{collapsed ? "Show" : "Hide"}
        </Button>
        <Button variant="ghost" size="icon" onClick={dismiss} aria-label="Dismiss getting started" data-testid="gs-dismiss-btn"><X size={16} /></Button>
      </div>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div id="gs-steps" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25, ease: EASE }} className="overflow-hidden">
            {data.complete ? (
              <div className="mt-4 flex flex-wrap items-center gap-3 rounded-[6px] bg-success-tint/60 px-4 py-3" data-testid="gs-complete">
                <PartyPopper size={18} className="text-success" aria-hidden="true" />
                <p className="flex-1 text-sm text-ink">You're all set. You've covered the essentials.</p>
                <Button variant="link" className="h-auto p-0" onClick={() => nav("/app?tour=1")} data-testid="gs-replay-tour">Replay tour</Button>
              </div>
            ) : null}
            <ol className="mt-4 grid gap-2 md:grid-cols-2">{data.steps.map((s, i) => <Step key={s.key} s={s} i={i} fresh={fresh.includes(s.key)} />)}</ol>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

export function ProgressRing({ done, total, size = 28 }) {
  const r = size / 2 - 3, c = 2 * Math.PI * r, pct = total ? done / total : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line, #E3DED3)" strokeWidth="3" />
      <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={pct === 1 ? "#2F6B45" : "var(--brand, #2B4C6F)"} strokeWidth="3" strokeLinecap="round"
        strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - pct) }} transition={{ duration: 0.6, ease: EASE }} />
    </svg>
  );
}

export function GettingStartedRing() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data } = useGettingStarted();
  if (!data) return null;
  const open = async () => {
    await reopenGettingStarted(qc);
    nav("/app");
    setTimeout(() => document.getElementById("getting-started")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  };
  return (
    <button type="button" onClick={open} className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-ink-secondary transition-colors duration-150 hover:bg-subtle hover:text-ink"
      aria-label={`Getting started: ${data.done} of ${data.total} done. Open checklist`} data-testid="gs-ring">
      <ProgressRing done={data.done} total={data.total} />
      <span data-testid="gs-ring-text">{data.done}/{data.total}</span>
    </button>
  );
}
